import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import LucideIcon from './LucideIcon';
import { TrackDefinition } from '../config/tracksCatalog';

export type TrackStatus = 'not-started' | 'in-progress' | 'complete' | 'locked';

interface TrackCardProps {
  track: TrackDefinition;
  status: TrackStatus;
  completed: number;
  total: number;
  onClick?: () => void;
  lockedReason?: string;
}

const STATUS_LABEL: Record<TrackStatus, string> = {
  'not-started': 'Not started',
  'in-progress': 'In progress',
  complete: 'Complete',
  locked: 'Locked',
};

/**
 * Shared card used by both the Frameworks and Production catalogs -- their
 * visual structure (title/status/description/tags/progress bar) is nearly
 * identical, per docs/build-plan/03-core-learning-app.md's own note that
 * these two screens should share one card component rather than duplicate it.
 */
const TrackCard: React.FC<TrackCardProps> = ({ track, status, completed, total, onClick, lockedReason }) => {
  const { isDarkMode } = useTheme();
  const locked = status === 'locked';
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div
      role={locked ? undefined : 'button'}
      tabIndex={locked ? undefined : 0}
      onClick={locked ? undefined : onClick}
      onKeyDown={(e) => {
        if (!locked && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      className="ws-fade-up"
      style={{
        background: 'var(--surface)',
        border: status === 'in-progress' ? '1.5px solid var(--ember)' : '1px solid var(--border)',
        borderRadius: 14,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        opacity: locked ? 0.85 : 1,
        cursor: locked ? 'default' : 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {track.trackNumber && (
              <span
                className="ws-mono"
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: 'var(--surface-sunken)',
                  border: '1px solid var(--border)',
                  color: 'var(--ember-strong)',
                }}
              >
                TRACK {String(track.trackNumber).padStart(2, '0')}
              </span>
            )}
            {track.difficulty && (
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-tertiary)' }}>
                {track.difficulty} {track.estimatedTime ? `· ${track.estimatedTime}` : ''}
              </span>
            )}
          </div>
          <h3 className="ws-display" style={{ margin: '2px 0 0', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
            {track.name}
          </h3>
        </div>

        <span
          className="ws-mono"
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            padding: '3px 8px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color:
              status === 'complete' ? 'var(--success)' : status === 'in-progress' ? 'var(--ember-strong)' : 'var(--text-tertiary)',
            background:
              status === 'complete'
                ? 'oklch(0.65 0.15 155 / 0.14)'
                : status === 'in-progress'
                ? 'var(--ember-soft)'
                : 'var(--surface-sunken)',
            border: `1px solid ${
              status === 'complete' ? 'var(--success)' : status === 'in-progress' ? 'var(--ember)' : 'var(--border)'
            }`,
          }}
        >
          {locked && <LucideIcon name="status-locked" size={10} />}
          {status === 'complete' && <LucideIcon name="status-check" size={10} />}
          {STATUS_LABEL[status]}
        </span>
      </div>

      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)' }}>{track.description}</p>

      {track.outcomes && track.outcomes.length > 0 && (
        <div style={{ background: 'var(--surface-sunken)', borderRadius: 8, padding: '8px 10px', fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>You'll build: </span>
          {track.outcomes[0]}
        </div>
      )}

      {locked && lockedReason ? (
        <p className="ws-mono" style={{ margin: 0, fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
          {lockedReason}
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {track.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'var(--surface-sunken)',
                  border: '1px solid var(--border)',
                  borderRadius: 999,
                  padding: '3px 9px',
                }}
              >
                {tag}
              </span>
            ))}
          </div>

          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 5 }}>
                <span>{completed}/{total} lessons</span>
                <span>{pct}%</span>
              </div>
              <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-sunken)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: status === 'complete' ? 'var(--success)' : 'var(--ember)' }} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ember)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {status === 'complete' ? 'Review Track' : status === 'in-progress' ? 'Continue Track' : 'Start Track'} &rarr;
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TrackCard;
